import { Injectable } from '@angular/core';
import { WebServiceService } from './web-service.service';
import { LocalStorageService } from './storage/local-storage.service';

@Injectable()
export class EmpCostCenterService {
  constructor(private webService: WebServiceService, private localStorage: LocalStorageService) {}

  companyId() {
    let companyId = this.localStorage.getEmpCompanyId();
    return companyId == undefined || companyId == null ? '0' : String(companyId);
  }

  prepare(host) {
    if (!host.costcenters) {
      host.costcenters = [];
    }
    if (!host.costCenterMisses) {
      host.costCenterMisses = {};
    }
    if (!host.costCenterSearchDone) {
      host.costCenterSearchDone = {};
    }
    if (host.costCenterMemoryCompanyId == undefined) {
      host.costCenterMemoryCompanyId = '';
    }
    if (host.costCenterOpenFetchDone == undefined) {
      host.costCenterOpenFetchDone = false;
    }
    if (host.empCostCenterOpenCount == undefined) {
      host.empCostCenterOpenCount = 0;
    }
    if (host.empCostCenterOpenId == undefined) {
      host.empCostCenterOpenId = '';
    }
  }

  resetForCompany(host) {
    this.prepare(host);
    let companyId = this.companyId();
    if (host.costCenterMemoryCompanyId == companyId) {
      return;
    }
    host.costCenterMemoryCompanyId = companyId;
    host.costcenters = [];
    host.costCenterMisses = {};
    host.costCenterSearchDone = {};
    host.costCenterOpenFetchDone = false;
    host.empCostCenterOpenCount = 0;
    host.empCostCenterOpenId = '';
    host.costCenterQueue = null;
    host.costCenterFlight = null;
    this.publish(host);
  }

  isEmployeeFilteredShipDropdown(host) {
    return host.page_url == '/direct-invoice' || host.page_url == '/zero-touch-invoice';
  }

  normalizeIds(ids) {
    let normalized = [];
    for (let id of (ids || [])) {
      if (id == undefined || id == null || String(id) === '') {
        continue;
      }
      let value = String(id);
      if (normalized.indexOf(value) === -1) {
        normalized.push(value);
      }
    }
    return normalized;
  }

  hasCostCenter(host, id) {
    let value = String(id);
    return (host.costcenters || []).some((row) => row && String(row.costcenter_id) === value);
  }

  isResolved(host, id) {
    this.prepare(host);
    if (id == undefined || id == null || String(id) === '') {
      return true;
    }
    let value = String(id);
    return !!host.costCenterMisses[value] || this.hasCostCenter(host, value);
  }

  trustedRows(host) {
    let rows = [];
    if (this.isEmployeeFilteredShipDropdown(host)) {
      rows = rows.concat(host.ShipToLocationArray || []);
    }
    if (host.getTrustedEmployeeLocations) {
      rows = rows.concat(host.getTrustedEmployeeLocations() || []);
    }
    return rows;
  }

  findRow(rows, id) {
    let value = String(id);
    return (rows || []).find((row) => row && String(row.costcenter_id || row.id) === value);
  }

  searchName(host, row) {
    let setup = host.company_setup || {};
    let name = row.location_nickname_9_digit + " (" + row.location_code_9_digit + ")";
    if (row.location_gst && setup.display_gstin_with_cost_center_and_vendor == '1') {
      name = name + "(" + setup.gstin_display_label + " - " + row.gst_no + ")";
    }
    if (setup.display_gst_number_in_bill_to_and_ship_to_locations == '1' && row.gst_no) {
      name = name + " (GST - " + row.gst_no + ")";
    }
    return name;
  }

  mapRecord(host, source) {
    let gstNo = source.gst_no ? source.gst_no : '';
    let nickname = source.location_nickname_9_digit || source.nick_name || '';
    let code = source.location_code_9_digit || source.location_code || source['9digit_code'] || '';
    let limit = source.direct_invoice_limit;
    let row: any = {
      costcenter_id: source.costcenter_id || source.cost_center_id || source.id || '',
      gst_no: gstNo,
      location_gst: gstNo ? String(gstNo).substr(0, 2) : '',
      location_code_9_digit: code,
      location_nickname_9_digit: nickname,
      direct_invoice_limit: (limit && String(limit) !== '0') ? limit : '',
      is_administrative_location: source.is_administrative_location ? source.is_administrative_location : '0',
      sez: source.sez ? source.sez : '0',
      primary_location_id: source.primary_location_id || source.location_id || '',
      company_id: source.company_id || this.companyId(),
      lob_id: source.lob_id || '',
      dept_id: source.dept_id || source.department_id || '',
      isUgstApplicable: source.isUgstApplicable ? source.isUgstApplicable : '0',
      id: source.id || source.costcenter_id || source.cost_center_id || ''
    };
    row.costcenter_id = row.costcenter_id ? String(row.costcenter_id) : '';
    row.cost_center_id = row.costcenter_id;
    row.nick_name = nickname;
    row.location_9digit_code = code;
    row.cost_center_name = source.cost_center_name || nickname;
    row.name = nickname + "(" + code + ")";
    row.loc_code_9_digit = 'loc_' + code;
    row.is_sez = source.is_sez || row.sez;
    row.isChecked = false;
    row.searchName = source.searchName ? source.searchName : this.searchName(host, row);
    return row;
  }

  fillGaps(existing, incoming) {
    let fields = ['gst_no', 'location_gst', 'location_code_9_digit', 'location_nickname_9_digit', 'searchName', 'sez', 'is_sez', 'primary_location_id', 'company_id', 'lob_id', 'dept_id', 'isUgstApplicable', 'is_administrative_location', 'cost_center_id', 'nick_name', 'location_9digit_code', 'cost_center_name', 'name', 'loc_code_9_digit'];
    for (let field of fields) {
      if ((!existing[field] || existing[field] === '0' && field === 'sez') && incoming[field]) {
        existing[field] = incoming[field];
      }
    }
    if (incoming.direct_invoice_limit && String(incoming.direct_invoice_limit) !== '0') {
      existing.direct_invoice_limit = incoming.direct_invoice_limit;
    } else if (!existing.direct_invoice_limit) {
      existing.direct_invoice_limit = '';
    }
  }

  mergeTrusted(host, id) {
    if (!this.hasCostCenter(host, id)) {
      return;
    }
    let trusted = this.findRow(this.trustedRows(host), id);
    if (!trusted) {
      return;
    }
    let existing = (host.costcenters || []).find((item) => item && String(item.costcenter_id) === String(id));
    if (existing) {
      this.fillGaps(existing, this.mapRecord(host, trusted));
    }
  }

  storeRow(host, raw, requestedIds) {
    let row = this.mapRecord(host, raw);
    if (!row.costcenter_id) {
      return '';
    }
    let requested = (requestedIds || []).indexOf(row.costcenter_id) !== -1;
    let setup = host.company_setup || {};
    let adminFlag = setup.show_is_administrative_location_in_ship_location;
    if ((adminFlag == undefined || adminFlag == null || adminFlag === '') && setup.RIT_show_is_administrative_location_in_ship_location != undefined) {
      adminFlag = setup.RIT_show_is_administrative_location_in_ship_location;
    }
    let hideAdmin = adminFlag == '0';
    if (!requested && hideAdmin && String(row.is_administrative_location) !== '0') {
      return '';
    }
    let dropdownRow = this.findRow(host.ShipToLocationArray || [], row.costcenter_id);
    let trusted = this.findRow(host.getTrustedEmployeeLocations ? host.getTrustedEmployeeLocations() : [], row.costcenter_id);
    if (dropdownRow) {
      let mapped = this.mapRecord(host, dropdownRow);
      if (mapped.direct_invoice_limit) {
        row.direct_invoice_limit = mapped.direct_invoice_limit;
      }
      this.fillGaps(row, mapped);
    }
    if (trusted) {
      let mappedTrusted = this.mapRecord(host, trusted);
      if (mappedTrusted.direct_invoice_limit) {
        row.direct_invoice_limit = mappedTrusted.direct_invoice_limit;
      }
      this.fillGaps(row, mappedTrusted);
    }
    let existing = (host.costcenters || []).find((item) => item && String(item.costcenter_id) === row.costcenter_id);
    if (existing) {
      this.fillGaps(existing, row);
      return row.costcenter_id;
    }
    host.costcenters.push(row);
    return row.costcenter_id;
  }

  copyKnownLocation(host, id) {
    let row = this.findRow(this.trustedRows(host), id);
    if (!row) {
      return false;
    }
    this.storeRow(host, row, [String(id)]);
    host.costcenters = this.webService.removeDuplicates(host.costcenters, 'costcenter_id');
    this.publish(host);
    return this.hasCostCenter(host, id);
  }

  publish(host) {
    if (host.onCostCentersUpdated) {
      host.onCostCentersUpdated();
    }
  }

  whenReady(host, ids, done) {
    this.prepare(host);
    let pending = this.normalizeIds(ids).filter((id) => !this.isResolved(host, id));
    if (!pending.length) {
      return false;
    }
    this.ensure(host, pending, '', done, false);
    return true;
  }

  ensure(host, ids, searchQuery, done, isOpenFetch?) {
    this.resetForCompany(host);
    let requested = this.normalizeIds(ids);
    let stillNeeded = [];
    for (let id of requested) {
      this.mergeTrusted(host, id);
      if (this.isResolved(host, id) || this.copyKnownLocation(host, id)) {
        continue;
      }
      stillNeeded.push(id);
    }
    let search = searchQuery ? String(searchQuery) : '';
    let searchNeeded = !!search && !host.costCenterSearchDone[search];
    let openNeeded = !!isOpenFetch && !host.costCenterOpenFetchDone;
    if (!stillNeeded.length && !searchNeeded && !openNeeded) {
      if (done) {
        done();
      }
      return;
    }
    if (host.costCenterFlight) {
      let flightHasIds = stillNeeded.every((id) => host.costCenterFlight.ids.indexOf(id) !== -1);
      let flightHasSearch = !searchNeeded || (host.costCenterFlight.searches || []).indexOf(search) !== -1;
      if (flightHasIds && flightHasSearch && !openNeeded) {
        if (done) {
          host.costCenterFlight.callbacks.push(done);
        }
        return;
      }
      this.queue(host, stillNeeded, searchNeeded ? search : '', done, openNeeded);
      return;
    }
    this.queue(host, stillNeeded, searchNeeded ? search : '', done, openNeeded);
  }

  queue(host, ids, search, done, isOpenFetch) {
    if (!host.costCenterQueue) {
      host.costCenterQueue = { ids: [], searches: [], callbacks: [], openFetch: false, timer: null };
    }
    for (let id of this.normalizeIds(ids)) {
      if (host.costCenterQueue.ids.indexOf(id) === -1) {
        host.costCenterQueue.ids.push(id);
      }
    }
    if (search && host.costCenterQueue.searches.indexOf(search) === -1) {
      host.costCenterQueue.searches.push(search);
    }
    if (done) {
      host.costCenterQueue.callbacks.push(done);
    }
    if (isOpenFetch) {
      host.costCenterQueue.openFetch = true;
    }
    if (!host.costCenterQueue.timer && !host.costCenterFlight) {
      host.costCenterQueue.timer = setTimeout(() => this.flush(host), 0);
    }
  }

  flush(host) {
    if (host.costCenterFlight || !host.costCenterQueue) {
      return;
    }
    let queued = host.costCenterQueue;
    host.costCenterQueue = null;
    queued.ids = (queued.ids || []).filter((id) => !this.isResolved(host, id));
    queued.searches = (queued.searches || []).filter((search) => search && !host.costCenterSearchDone[search]);
    if (!queued.ids.length && !queued.searches.length && !queued.openFetch) {
      for (let callback of (queued.callbacks || [])) {
        callback();
      }
      return;
    }
    let companyId = this.companyId();
    let search = queued.searches.length ? queued.searches[0] : '';
    host.costCenterFlight = queued;
    if (queued.searches.length > 1) {
      for (let extraSearch of queued.searches.slice(1)) {
        this.queue(host, [], extraSearch, null, false);
      }
      queued.searches = [search];
    }
    let requestObj = {
      "employee_id": this.localStorage.getUserId() ? this.localStorage.getUserId() : '0',
      "company_id": companyId,
      "is_backend_search": "1",
      "search_query": search,
      "item_in": queued.ids
    };
    let finish = () => {
      let callbacks = queued.callbacks || [];
      host.costCenterFlight = null;
      if (this.companyId() == companyId) {
        for (let callback of callbacks) {
          callback();
        }
      }
      if (host.costCenterQueue) {
        this.flush(host);
      }
    };
    let request = this.webService.commonPostMethod("employees/ajax_getemp_cost_centers", requestObj);
    if (!request) {
      for (let id of queued.ids) {
        host.costCenterMisses[id] = true;
      }
      if (search) {
        host.costCenterSearchDone[search] = true;
      }
      if (queued.openFetch) {
        host.costCenterOpenFetchDone = true;
      }
      finish();
      return;
    }
    request.subscribe(
      data => {
        if (this.companyId() != companyId) {
          host.costCenterFlight = null;
          host.costCenterQueue = null;
          return;
        }
        let rows = data && data.cost_centers ? data.cost_centers : [];
        let pushedIds = [];
        for (let row of rows) {
          let storedId = this.storeRow(host, row, queued.ids);
          if (storedId && pushedIds.indexOf(storedId) === -1) {
            pushedIds.push(storedId);
          }
        }
        host.costcenters = this.webService.removeDuplicates(host.costcenters || [], 'costcenter_id');
        this.publish(host);
        for (let id of queued.ids) {
          if (!this.hasCostCenter(host, id)) {
            host.costCenterMisses[id] = true;
          }
        }
        if (search) {
          host.costCenterSearchDone[search] = true;
        }
        if (queued.openFetch) {
          host.costCenterOpenFetchDone = true;
          host.empCostCenterOpenCount = pushedIds.length;
          host.empCostCenterOpenId = pushedIds.length == 1 ? pushedIds[0] : '';
        }
        finish();
      },
      error => {
        if (this.companyId() == companyId) {
          for (let id of queued.ids) {
            host.costCenterMisses[id] = true;
          }
          if (search) {
            host.costCenterSearchDone[search] = true;
          }
          if (queued.openFetch) {
            host.costCenterOpenFetchDone = true;
            host.empCostCenterOpenCount = 0;
            host.empCostCenterOpenId = '';
          }
        }
        finish();
      }
    );
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1914368402/src/src/app/shared/services/emp-cost-center.service.ts